import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Loader2, Download, Printer, ArrowLeft, FileText, ShieldAlert } from "lucide-react";
import api from "@/lib/api";

/**
 * Pagina protetta riservata al clinico (Laura) per consultare,
 * scaricare e stampare la documentazione tecnico-clinica delle
 * formule e degli indicatori usati in Revela.
 */
const ClinicalDocumentationPage = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get("/admin/clinical-documentation")
      .then((r) => { if (alive) setData(r.data); })
      .catch((err) => {
        if (!alive) return;
        if (err.response?.status === 403) setError("forbidden");
        else if (err.response?.status === 404) setError("not_found");
        else setError("generic");
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const handleDownload = async () => {
    try {
      const res = await api.get("/admin/clinical-documentation/download", {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/markdown" }));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", data?.filename || "REVELA_FORMULE_E_INDICATORI.md");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      // graceful fallback: copy URL to clipboard
      console.error(e);
    }
  };

  const handleDownloadDocx = async () => {
    try {
      const res = await api.get("/admin/clinical-documentation/download-docx", {
        responseType: "blob",
      });
      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const baseName = (data?.filename || "REVELA_FORMULE_E_INDICATORI.md").replace(/\.md$/i, "");
      link.setAttribute("download", `${baseName}.docx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error === "forbidden") {
    return (
      <div className="max-w-2xl mx-auto px-4 py-8" data-testid="clinical-doc-forbidden">
        <Card className="border-rose-200 bg-rose-50/50">
          <CardContent className="p-6 text-center space-y-2">
            <ShieldAlert className="w-10 h-10 mx-auto text-rose-500" />
            <h2 className="text-lg font-semibold text-rose-900">Accesso riservato</h2>
            <p className="text-sm text-rose-800/80">
              Questa pagina è disponibile solo all'account clinico.
            </p>
            <Button variant="outline" onClick={() => navigate("/oggi")} className="mt-3">
              <ArrowLeft className="w-4 h-4 mr-1.5" /> Torna alla home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-8 text-center">
        <p className="text-sm text-muted-foreground">
          Documentazione non disponibile. Riprova più tardi.
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-6 pb-24 md:pb-8" data-testid="clinical-documentation-page">
      {/* Header — non stampato */}
      <div className="space-y-4 print:hidden">
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(-1)}
            className="h-8 -ml-2"
            data-testid="clinical-doc-back"
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> Indietro
          </Button>
        </div>

        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <h1
              className="text-xl sm:text-2xl font-bold text-stone-800"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              <FileText className="inline-block w-5 h-5 mr-1 -mt-1" />
              Documentazione tecnico-clinica
            </h1>
            <p className="text-xs text-stone-500 mt-1">
              Formule, indicatori e soglie del modello Revela.
              {data.updated_at && (
                <span className="ml-1">
                  · Aggiornato il {new Date(data.updated_at).toLocaleDateString("it-IT")}
                </span>
              )}
            </p>
          </div>
          <div className="flex gap-2 shrink-0 flex-wrap">
            <Button
              size="sm"
              variant="outline"
              onClick={handleDownloadDocx}
              className="text-xs h-8"
              data-testid="clinical-doc-download-docx"
            >
              <Download className="w-3.5 h-3.5 mr-1" />
              Scarica Word (.docx)
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={handleDownload}
              className="text-xs h-8"
              data-testid="clinical-doc-download"
            >
              <Download className="w-3.5 h-3.5 mr-1" />
              Scarica (.md)
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={handlePrint}
              className="text-xs h-8"
              data-testid="clinical-doc-print"
            >
              <Printer className="w-3.5 h-3.5 mr-1" />
              Stampa
            </Button>
          </div>
        </div>
      </div>

      {/* Contenuto Markdown */}
      <article
        className="prose prose-stone prose-sm sm:prose-base max-w-none mt-6 bg-white rounded-lg border border-stone-200 p-4 sm:p-8 print:p-0 print:border-0
                   prose-headings:font-semibold prose-headings:text-stone-800
                   prose-h1:text-2xl prose-h1:mb-2
                   prose-h2:text-xl prose-h2:mt-8 prose-h2:mb-3 prose-h2:border-b prose-h2:border-stone-200 prose-h2:pb-1
                   prose-h3:text-lg prose-h3:mt-5 prose-h3:mb-2
                   prose-table:text-xs prose-table:my-3
                   prose-th:bg-stone-100 prose-th:px-2 prose-th:py-1.5 prose-th:text-left
                   prose-td:px-2 prose-td:py-1.5 prose-td:border-t prose-td:border-stone-200
                   prose-code:before:hidden prose-code:after:hidden prose-code:bg-stone-100 prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-code:text-[0.85em]
                   prose-pre:bg-stone-50 prose-pre:border prose-pre:border-stone-200
                   prose-blockquote:border-l-amber-300 prose-blockquote:bg-amber-50/40 prose-blockquote:py-1 prose-blockquote:px-3 prose-blockquote:not-italic
                   prose-li:my-0.5"
        data-testid="clinical-doc-content"
      >
        <ReactMarkdown remarkPlugins={[remarkGfm]}>
          {data.content}
        </ReactMarkdown>
      </article>

      {/* Print stylesheet */}
      <style>{`
        @media print {
          body { background: white !important; }
          .print\\:hidden { display: none !important; }
          article { box-shadow: none !important; }
          @page { margin: 1.5cm; }
        }
      `}</style>
    </div>
  );
};

export default ClinicalDocumentationPage;
